/**
 * Preview cache manager using a single sessionStorage key.
 * This approach makes future migration to localStorage simpler.
 */

export interface PreviewData {
    previewId: string;
    previewHtmlFileToken?: string | null;
    encryptedApplicationId: string;
    previewHtmlContentHash?: string | null;
}

interface PreviewCache {
    [draftId: string]: PreviewData;
}

const CACHE_KEY = 'application_previews';

/**
 * Get all cached preview data
 */
function getCache(): PreviewCache {
    if (typeof window === 'undefined') {
        return {};
    }

    try {
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (!cached) return {};
        return JSON.parse(cached) as PreviewCache;
    } catch {
        // Clear corrupted cache data
        sessionStorage.removeItem(CACHE_KEY);
        return {};
    }
}

/**
 * Save entire cache to sessionStorage
 */
function saveCache(cache: PreviewCache): void {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
        // Clear cache if quota exceeded or other storage error
        console.warn('sessionStorage error, clearing cache', error);
        sessionStorage.removeItem(CACHE_KEY);
    }
}

/**
 * Validate that cached data has the correct structure
 */
function validatePreviewData(data: unknown): data is PreviewData {
    return (
        data !== null &&
        typeof data === 'object' &&
        'previewId' in data &&
        'encryptedApplicationId' in data &&
        typeof data.previewId === 'string' &&
        typeof data.encryptedApplicationId === 'string'
    );
}

/**
 * Get cached preview data for a specific draft
 * Returns null if not cached or if data is invalid
 */
export function getCachedPreview(draftId: string): PreviewData | null {
    const cache = getCache();
    const data = cache[draftId];

    if (!data) {
        return null;
    }

    // Validate the cached data structure
    if (!validatePreviewData(data)) {
        // Remove invalid data from cache
        delete cache[draftId];
        saveCache(cache);
        return null;
    }

    return data;
}

/**
 * Set cached preview data for a specific draft
 */
export function setCachedPreview(draftId: string, data: PreviewData): void {
    const cache = getCache();
    cache[draftId] = data;
    saveCache(cache);
}

/**
 * Remove cached preview data for a specific draft
 */
export function deleteCachedPreview(draftId: string): void {
    const cache = getCache();
    if (draftId in cache) {
        delete cache[draftId];
        saveCache(cache);
    }
}

/**
 * Remove cached preview data for drafts not in the provided list
 */
export function cleanupStaleCache(validDraftIds: string[]): void {
    const cache = getCache();
    const cachedIds = Object.keys(cache);
    const validSet = new Set(validDraftIds);

    let hasChanges = false;
    for (const cachedId of cachedIds) {
        if (!validSet.has(cachedId)) {
            delete cache[cachedId];
            hasChanges = true;
        }
    }

    if (hasChanges) {
        saveCache(cache);
    }
}
